import { isSameDay } from 'date-fns'
import type { TFunction } from 'i18next'
import Personnummer from 'personnummer'
import { type CurrencyCode } from '@/services/graphql/generated'
import { type IsoLocale } from '@/utils/l10n/types'

type MoneyFormatOptions = { locale: IsoLocale }

export type Money = {
  amount: number
  currencyCode: CurrencyCode
}

const formatMoney = (money: Money, options: MoneyFormatOptions): string => {
  return (
    new Intl.NumberFormat(options.locale, {
      style: 'currency',
      currency: money.currencyCode,
      // Same strategy used by the API to round prices
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      roundingMode: 'halfEven',
    })
      .format(money.amount)
      // Hacky solution: API returns prices with dot as decimal separator but current support locales will use comma
      .replace(',', '.')
  )
}

// 100kr/mo.
const formatMonthlyPrice = (price: Money, options: MoneyFormatOptions & { t: TFunction }): string => {
  const displayAmount = formatMoney(price, options)
  return options.t('MONTHLY_PRICE', {
    displayAmount,
    defaultValue: `MONTHLY_PRICE ${displayAmount}`,
  })
}

// "100 kr per month" — screen-reader-friendly, unabbreviated form
const formatMonthlyPriceAccessible = (price: Money, options: MoneyFormatOptions & { t: TFunction }): string => {
  const displayAmount = formatMoney(price, options)
  return options.t('MONTHLY_PRICE_ACCESSIBLE', {
    displayAmount,
    defaultValue: `MONTHLY_PRICE_ACCESSIBLE ${displayAmount}`,
  })
}

// 100kr/year
const formatAnnualAmount = (amount: Money, options: MoneyFormatOptions & { t: TFunction }): string => {
  const displayAmount = formatMoney(amount, options)
  return options.t('ANNUAL_AMOUNT', {
    displayAmount,
    defaultValue: `ANNUAL_AMOUNT ${displayAmount}`,
  })
}

// 100kr per year
const formatAnnualReduction = (price: Money, options: MoneyFormatOptions & { t: TFunction }): string => {
  const displayAmount = formatMoney({ ...price, amount: price.amount * 12 }, options)
  return options.t('ANNUAL_REDUCTION', {
    displayAmount,
    defaultValue: `ANNUAL_REDUCTION ${displayAmount}`,
  })
}

type DateFormatOptions = { locale: IsoLocale } & { t: TFunction }

const SHORT_MONTHS = {
  en: ['Jan', 'Feb', 'Mar', 'April', 'May', 'June', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'],
  sv: ['jan', 'feb', 'mars', 'april', 'maj', 'juni', 'juli', 'aug', 'sept', 'okt', 'nov', 'dec'],
} as const

const formatShortDate = (date: Date, locale: IsoLocale): string => {
  const stockholmDate = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Stockholm' }))

  const day = stockholmDate.getDate()
  const year = stockholmDate.getFullYear()
  const monthIndex = stockholmDate.getMonth()

  const months = locale.startsWith('en') ? SHORT_MONTHS.en : SHORT_MONTHS.sv
  return `${day} ${months[monthIndex]} ${year}`
}

const formatRelativeDate = (date: Date, options: DateFormatOptions): string => {
  if (isSameDay(date, new Date())) {
    return options.t('DATE_SAME_DAY')
  }
  return formatShortDate(date, options.locale)
}

const formatTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

const formatSsn = (ssn: string): string => {
  try {
    const longFormat = Personnummer.parse(ssn).format(true)
    // replace the last 4 digits with asterisks
    return longFormat.replace(/(\d{4})$/, '-****')
  } catch (error) {
    console.warn(`Could not format SSN: ${ssn}`, error)
    return ssn
  }
}

const formatCarRegistrationNumber = (regNumber: string): string => {
  // add space after 3rd character
  return regNumber.toUpperCase().replace(/(.{3})/, '$1 ')
}

const formatZipCode = (value: string): string => {
  return value.replace(/(\d{3})(\d{2})/, '$1 $2')
}

const formatNumberGrouping = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { useGrouping: true }).format(value)

type FormatterOptions = MoneyFormatOptions & { t: TFunction }

export class Formatter {
  constructor(public options: FormatterOptions) {
    // Has to be assigned explicitly for Storybook compatibility, looks like it messes up TS constructor properties
    this.options = options
  }

  public money = (money: Money) => formatMoney(money, this.options)
  public monthlyPrice = (price: Money) => formatMonthlyPrice(price, this.options)
  public monthlyPriceAccessible = (price: Money) => formatMonthlyPriceAccessible(price, this.options)
  public annualAmount = (amount: Money) => formatAnnualAmount(amount, this.options)
  public annualReduction = (price: Money) => formatAnnualReduction(price, this.options)
  public fromNow = (date: Date) => formatRelativeDate(date, this.options)
  public shortDate = (date: Date) => formatShortDate(date, this.options.locale)
  public dateFull = (date: Date, options?: { hideYear?: boolean; abbreviateMonth?: boolean }) => {
    const formattedDate = date.toLocaleDateString(this.options.locale, {
      year: options?.hideYear ? undefined : 'numeric',
      month: options?.abbreviateMonth ? 'short' : 'long',
      day: 'numeric',
      timeZone: 'Europe/Stockholm',
    })

    // Removes '.' used for abbreviations
    if (options?.abbreviateMonth) {
      return formattedDate.replace('.', '')
    }

    return formattedDate
  }

  public titleCase = (str: string) => formatTitleCase(str)
  public ssn = formatSsn
  public carRegistrationNumber = formatCarRegistrationNumber
  public zipCode = formatZipCode
  public numberGrouping = (value: number) => formatNumberGrouping(value, this.options.locale)
}
